import * as THREE from 'three';
import { AssetManager } from './Assets';

const MIN_STRAIN_THRESHOLD = 1e-4;
const PERCENTILE_TARGET = 0.95;
const DEFAULT_SCRATCH_STRAIN_CAPACITY = 4096;

function quickselect(arr: Float32Array, left: number, right: number, k: number): number {
  while (left < right) {
    const pivotIndex = left + Math.floor(Math.random() * (right - left + 1));
    const pivotValue = arr[pivotIndex];
    arr[pivotIndex] = arr[right];
    arr[right] = pivotValue;

    let storeIndex = left;
    for (let i = left; i < right; i++) {
      if (arr[i] < pivotValue) {
        const tmp = arr[storeIndex];
        arr[storeIndex] = arr[i];
        arr[i] = tmp;
        storeIndex++;
      }
    }
    arr[right] = arr[storeIndex];
    arr[storeIndex] = pivotValue;

    if (k === storeIndex) {
      return arr[k];
    } else if (k < storeIndex) {
      right = storeIndex - 1;
    } else {
      left = storeIndex + 1;
    }
  }
  return arr[left];
}

export interface Entity {
  id: string;
  type: 'log' | 'tool' | 'support' | 'wedge';
  mesh: THREE.Object3D;
  points?: THREE.Points;
  wireframe?: THREE.Mesh;
  vertexMap?: number[][]; // Map physics node index -> array of THREE.js vertex indices
  segments?: any[]; // Store the ring topology for longitudinal axis calculation
  endcapNodeIndices?: Set<number>; // Node indices of dynamic cut endcaps
  dynamicIndices?: {
    buffer: Uint32Array;
    staticIndices: { bark: number[], top: number[], bottom: number[] };
    kerfs: {
      springIndices: number[];
      longitudinalSpringIndices?: number[];
      bridgeIndices: number[][]; // Bark faces covering the gap for each slice
      endcapIndices: number[][]; // Wood faces inside the gap for each slice
      globalKerfIndex: number;
    }[];
  };
}

export class EntityManager {
  private entities = new Map<string, Entity>();
  private scene: THREE.Scene;
  private defaultLogMaterials: THREE.Material[] | null = null;
  public globalSprings: Int32Array | null = null;
  public activeSprings: Uint32Array | null = null;
  public globalSpringsMesh: THREE.LineSegments | null = null;
  private scratchPositiveStrains = new Float32Array(DEFAULT_SCRATCH_STRAIN_CAPACITY);
  private scratchNegativeStrains = new Float32Array(DEFAULT_SCRATCH_STRAIN_CAPACITY);

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public add(entity: Entity) {
    if (this.entities.has(entity.id)) {
      console.warn(`Entity with ID ${entity.id} already exists.`);
      return;
    }
    
    // Auto-generate Points and Wireframe objects for debugging if it's a mesh
    if (entity.mesh instanceof THREE.Mesh) {
       const pointsMat = new THREE.PointsMaterial({ color: 0xff0000, size: 0.03, depthTest: false, transparent: true });
       const points = new THREE.Points(entity.mesh.geometry, pointsMat);
       points.visible = false;
       this.scene.add(points);
       entity.points = points;
       
       const wireMat = new THREE.MeshBasicMaterial({ color: 0x00ff00, wireframe: true, depthTest: false, transparent: true });
       const wireMesh = new THREE.Mesh(entity.mesh.geometry, wireMat);
       wireMesh.visible = false;
       this.scene.add(wireMesh);
       entity.wireframe = wireMesh;
    }

    this.entities.set(entity.id, entity);
    this.scene.add(entity.mesh);
  }

  public remove(id: string) {
    const entity = this.entities.get(id);
    if (entity) {
      // 1. Remove main mesh from scene and dispose geometry/materials
      this.scene.remove(entity.mesh);
      if (entity.mesh instanceof THREE.Mesh) {
        if (entity.mesh.geometry) {
          entity.mesh.geometry.dispose();
        }
        if (entity.mesh.material) {
          const materials = Array.isArray(entity.mesh.material) ? entity.mesh.material : [entity.mesh.material];
          for (const mat of materials) {
            mat.dispose();
          }
        }
      }

      // 2. Remove and dispose debug wireframe (shares mesh geometry; only dispose material)
      if (entity.wireframe) {
        this.scene.remove(entity.wireframe);
        if (entity.wireframe.material) {
          const materials = Array.isArray(entity.wireframe.material) ? entity.wireframe.material : [entity.wireframe.material];
          for (const mat of materials) {
            mat.dispose();
          }
        }
      }

      // 3. Remove and dispose debug points (shares mesh geometry; only dispose material)
      if (entity.points) {
        this.scene.remove(entity.points);
        if (entity.points.material) {
          const materials = Array.isArray(entity.points.material) ? entity.points.material : [entity.points.material];
          for (const mat of materials) {
            mat.dispose();
          }
        }
      }

      this.entities.delete(id);
    }
  }

  public clearLogs() {
    for (const [id, entity] of this.entities.entries()) {
      if (entity.type === 'log') {
        this.remove(id);
      }
    }
  }

  public clearWedges() {
    for (const [id, entity] of this.entities.entries()) {
      if (entity.type === 'wedge') {
        this.remove(id);
      }
    }
  }

  public get(id: string): Entity | undefined {
    return this.entities.get(id);
  }

  public getAll(): Entity[] {
    return Array.from(this.entities.values());
  }

  private getDefaultLogMaterials() {
    if (this.defaultLogMaterials) return this.defaultLogMaterials;
    
    const barkTexture = AssetManager.getTexture('/assets/textures/bark_willow_02_diff_4k.jpg');
    if (barkTexture) {
      barkTexture.wrapS = THREE.RepeatWrapping;
      barkTexture.wrapT = THREE.RepeatWrapping;
      barkTexture.repeat.set(1, 4);
    }
    const endcapTexture = AssetManager.getTexture('/assets/textures/endcap.jpg');
    
    const barkMat = new THREE.MeshStandardMaterial({ 
      map: barkTexture || null, 
      color: barkTexture ? 0xffffff : 0x8b5a2b,
      roughness: 0.8,
      side: THREE.DoubleSide
    });
    const endcapMat = new THREE.MeshStandardMaterial({ 
      map: endcapTexture || null, 
      color: endcapTexture ? 0xffffff : 0xd2b48c,
      roughness: 0.9,
      side: THREE.DoubleSide
    });
    
    this.defaultLogMaterials = [barkMat, endcapMat, endcapMat];
    return this.defaultLogMaterials;
  }

  public syncTopology(segments: any[][], kerfs: any[] = [], springs?: Int32Array, positions?: Float32Array) {
    if (springs) {
      this.globalSprings = springs;
      if (!this.globalSpringsMesh) {
        const material = new THREE.LineBasicMaterial({ color: 0x00ff00, depthTest: false, transparent: true, opacity: 0.5 });
        const geometry = new THREE.BufferGeometry();
        this.globalSpringsMesh = new THREE.LineSegments(geometry, material);
        this.scene.add(this.globalSpringsMesh);
      }
    }
    
    for (let i = 0; i < segments.length; i++) {
      const id = `log-${i}`;
      if (!this.entities.has(id)) {
        const mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.getDefaultLogMaterials());
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        this.add({ id, type: 'log', mesh, segments: segments[i] });
      } else {
        const entity = this.entities.get(id);
        if (entity) entity.segments = segments[i];
      }
      this.rebuildMesh(id, [segments[i]], kerfs, i, positions);
    }

    // Remove obsolete log entities if they exceed segments length
    for (const entity of this.getAll()) {
      if (entity.type === 'log') {
        const idx = parseInt(entity.id.split('-')[1]);
        if (idx >= segments.length) {
          this.remove(entity.id);
        }
      }
    }
  }

  public rebuildMesh(id: string, segments: any[][], kerfs: any[] = [], segmentIndex: number = 0, positions?: Float32Array) {
    const entity = this.entities.get(id);
    if (!entity || !(entity.mesh instanceof THREE.Mesh)) return;

    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const uvs: number[] = [];
    
    // We will separate static indices from dynamic kerf indices
    const staticBarkIndices: number[] = [];
    const staticTopEndcapIndices: number[] = [];
    const staticBottomEndcapIndices: number[] = [];
    
    const dynamicKerfs: any[] = [];
    
    const vertexMap: number[][] = [];
    let vCount = 0;
    
    const addVertex = (physIdx: number, u: number, v: number) => {
      // Pre-fill vertex position from last known physics state to avoid a one-frame flash at origin
      if (positions && physIdx * 3 + 2 < positions.length) {
        vertices.push(positions[physIdx * 3], positions[physIdx * 3 + 1], positions[physIdx * 3 + 2]);
      } else {
        vertices.push(0, 0, 0);
      }
      uvs.push(u, v);
      if (!vertexMap[physIdx]) vertexMap[physIdx] = [];
      vertexMap[physIdx].push(vCount);
      vCount++;
      return vCount - 1;
    };

    segments.forEach((segment) => {
      const ringsCount = segment.length;
      if (ringsCount < 2) return;
      const nodesPerRing = segment[0].shellIndices.length;

      // 1. Bark (Sides)
      const barkVertexIndices: number[][] = [];
      for (let r = 0; r < ringsCount; r++) {
        barkVertexIndices[r] = [];
        const ring = segment[r];
        const v = ring.originalV;
        for (let j = 0; j <= nodesPerRing; j++) {
          const u = j / nodesPerRing;
          const physIdx = ring.shellIndices[j % nodesPerRing];
          barkVertexIndices[r].push(addVertex(physIdx, u, v));
        }
      }
      
      for (let r = 0; r < ringsCount - 1; r++) {
        for (let j = 0; j < nodesPerRing; j++) {
          const a = barkVertexIndices[r][j];
          const b = barkVertexIndices[r+1][j];
          const c = barkVertexIndices[r+1][j+1];
          const d = barkVertexIndices[r][j+1];
          staticBarkIndices.push(a, b, c);
          staticBarkIndices.push(a, c, d);
        }
      }

      // 2. Endcaps
      const addEndcap = (ring: any, isTop: boolean, isDynamic: boolean = false): number[] => {
        const localIndices: number[] = [];
        const centerVIdx = addVertex(ring.pithIdx, 0.5, 0.5);
        const perimeterIndices: number[] = [];
        for (let j = 0; j < nodesPerRing; j++) {
          const theta = (j / nodesPerRing) * Math.PI * 2;
          const uvX = 0.5 + Math.sin(theta) * 0.5;
          const uvY = 0.5 + Math.cos(theta) * 0.5;
          perimeterIndices.push(addVertex(ring.shellIndices[j], uvX, uvY));
        }
        for (let j = 0; j < nodesPerRing; j++) {
          const a = centerVIdx;
          const b = perimeterIndices[j];
          const c = perimeterIndices[(j + 1) % nodesPerRing];
          if (isTop) localIndices.push(a, c, b);
          else localIndices.push(a, b, c);
        }
        
        if (!isDynamic) {
          if (isTop) staticTopEndcapIndices.push(...localIndices);
          else staticBottomEndcapIndices.push(...localIndices);
        }
        return localIndices;
      };

      // Find if this segment connects to any kerfs
      const lastRing = segment[ringsCount - 1];
      const attachedKerfIdx = kerfs.findIndex(k => k.leftRing.pithIdx === lastRing.pithIdx);
      const attachedKerf = kerfs[attachedKerfIdx];

      // Top endcap is always static for now
      addEndcap(segment[0], true, false); 
      
      if (!attachedKerf) {
        // Normal bottom endcap
        addEndcap(lastRing, false, false);
      } else {
        // This segment is the LEFT side of a kerf.
        // We will build dynamic structures for the kerf.
        const c1EndcapIndices = addEndcap(attachedKerf.leftRing, false, true); // Hidden initially
        
        // Borrow the rightRing (C2) vertices to draw the bark bridge
        const rightRingVIndices: number[] = [];
        for (let j = 0; j <= nodesPerRing; j++) {
          const u = j / nodesPerRing;
          const physIdx = attachedKerf.rightRing.shellIndices[j % nodesPerRing];
          rightRingVIndices.push(addVertex(physIdx, u, attachedKerf.rightRing.originalV)); // use originalV for bridge
        }
        
        // Build the bridge triangles per radial slice
        const bridgeSlices: number[][] = [];
        const endcapSlices: number[][] = [];
        
        for (let j = 0; j < nodesPerRing; j++) {
          const a = barkVertexIndices[ringsCount - 1][j]; // C1 shell
          const b = rightRingVIndices[j];                 // C2 shell
          const c = rightRingVIndices[j+1];               // C2 shell next
          const d = barkVertexIndices[ringsCount - 1][j+1]; // C1 shell next
          
          // Bark bridge for slice j
          bridgeSlices.push([a, b, c, a, c, d]);
          
          // Endcap for slice j (3 vertices per slice)
          // The endcap function generates triangles in order, 3 indices per slice
          endcapSlices.push([
            c1EndcapIndices[j * 3],
            c1EndcapIndices[j * 3 + 1],
            c1EndcapIndices[j * 3 + 2]
          ]);
        }
        
        dynamicKerfs.push({
          springIndices: attachedKerf.springIndices,
          longitudinalSpringIndices: attachedKerf.longitudinalSpringIndices,
          bridgeIndices: bridgeSlices,
          endcapIndices: endcapSlices,
          globalKerfIndex: attachedKerfIdx
        });
      }
    });

    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uvs), 2));
    
    // Allocate index buffer
    // Max possible size = static + (all bridge faces) + (all endcap faces)
    let maxIndices = staticBarkIndices.length + staticTopEndcapIndices.length + staticBottomEndcapIndices.length;
    for (const dk of dynamicKerfs) {
      for (const b of dk.bridgeIndices) maxIndices += b.length;
      for (const e of dk.endcapIndices) maxIndices += e.length;
    }
    
    const indexBuffer = new Uint32Array(maxIndices);
    geometry.setIndex(new THREE.BufferAttribute(indexBuffer, 1));
    
    entity.vertexMap = vertexMap;

    const endcapNodeIndices = new Set<number>();
    if (kerfs && kerfs.length > 0) {
      for (const k of kerfs) {
        if (k.leftRing) {
          endcapNodeIndices.add(k.leftRing.pithIdx);
          k.leftRing.shellIndices.forEach((idx: number) => endcapNodeIndices.add(idx));
        }
        if (k.rightRing) {
          endcapNodeIndices.add(k.rightRing.pithIdx);
          k.rightRing.shellIndices.forEach((idx: number) => endcapNodeIndices.add(idx));
        }
      }
    }
    entity.endcapNodeIndices = endcapNodeIndices;
    
    if (dynamicKerfs.length > 0) {
      entity.dynamicIndices = {
        buffer: indexBuffer,
        staticIndices: {
          bark: staticBarkIndices,
          top: staticTopEndcapIndices,
          bottom: staticBottomEndcapIndices
        },
        kerfs: dynamicKerfs
      };
      
      // Initial render state: all bridges visible, all dynamic endcaps hidden
      let offset = 0;
      
      // Bark + Bridges
      indexBuffer.set(staticBarkIndices, offset);
      offset += staticBarkIndices.length;
      for (const dk of dynamicKerfs) {
        for (const b of dk.bridgeIndices) {
          indexBuffer.set(b, offset);
          offset += b.length;
        }
      }
      const barkEnd = offset;
      
      // Top Endcaps
      indexBuffer.set(staticTopEndcapIndices, offset);
      offset += staticTopEndcapIndices.length;
      const topEndcapEnd = offset;
      
      // Bottom Endcaps
      indexBuffer.set(staticBottomEndcapIndices, offset);
      offset += staticBottomEndcapIndices.length;
      const bottomEndcapEnd = offset;
      
      geometry.setDrawRange(0, bottomEndcapEnd);
      geometry.addGroup(0, barkEnd, 0); // Bark Mat
      geometry.addGroup(barkEnd, topEndcapEnd - barkEnd, 1); // Top Mat
      geometry.addGroup(topEndcapEnd, bottomEndcapEnd - topEndcapEnd, 2); // Bottom Mat
    } else {
      entity.dynamicIndices = undefined;
      const allStatic = [...staticBarkIndices, ...staticTopEndcapIndices, ...staticBottomEndcapIndices];
      geometry.setIndex(allStatic);
      geometry.addGroup(0, staticBarkIndices.length, 0);
      geometry.addGroup(staticBarkIndices.length, staticTopEndcapIndices.length, 1);
      geometry.addGroup(staticBarkIndices.length + staticTopEndcapIndices.length, staticBottomEndcapIndices.length, 2);
    }
    
    // Reuse existing textured materials if possible
    let currentMaterials = entity.mesh.material;
    if (Array.isArray(currentMaterials) && currentMaterials.length >= 3) {
      currentMaterials.forEach(m => m.side = THREE.DoubleSide);
    } else {
      currentMaterials = this.getDefaultLogMaterials();
    }
    
    // Dispose old geometry and swap
    if (entity.mesh.geometry) entity.mesh.geometry.dispose();
    entity.mesh.geometry = geometry;
    entity.mesh.material = currentMaterials;
    
    let wireframeVisible = false;
    let pointsVisible = false;

    // Dispose old debug meshes (materials only; geometry is already disposed above)
    if (entity.wireframe) {
       wireframeVisible = entity.wireframe.visible;
       this.scene.remove(entity.wireframe);
       (entity.wireframe.material as THREE.Material).dispose();
    }
    if (entity.points) {
       pointsVisible = entity.points.visible;
       this.scene.remove(entity.points);
       (entity.points.material as THREE.Material).dispose();
    }

    // Create new debug meshes (sharing the same geometry)
    const wireMat = new THREE.MeshBasicMaterial({ color: 0xffaa00, wireframe: true });
    const pointsMat = new THREE.PointsMaterial({ color: 0xff0000, size: 0.1 });
    
    entity.wireframe = new THREE.Mesh(geometry, wireMat);
    entity.points = new THREE.Points(geometry, pointsMat);
    
    entity.wireframe.visible = wireframeVisible;
    entity.points.visible = pointsVisible;
    
    this.scene.add(entity.wireframe);
    this.scene.add(entity.points);
    
    console.log(`[EntityManager] Mesh rebuilt: ${geometry.attributes.position.count} vertices.`);
  }

  public syncPositions(id: string, positions: Float32Array, strains?: Float32Array, brokenKerfSprings?: boolean[][], globalTension: boolean = false) {
    const entity = this.entities.get(id);
    if (!entity || !entity.vertexMap || !(entity.mesh instanceof THREE.Mesh)) return;

    const geometry = entity.mesh.geometry as THREE.BufferGeometry;
    
    // 1. Dynamic Index Buffer Update
    if (entity.dynamicIndices && brokenKerfSprings) {
      const dyn = entity.dynamicIndices;
      let offset = 0;
      
      // Bark + Bridges
      dyn.buffer.set(dyn.staticIndices.bark, offset);
      offset += dyn.staticIndices.bark.length;
      
      for (const kerf of dyn.kerfs) {
        const kerfStates = brokenKerfSprings[kerf.globalKerfIndex];
        if (!kerfStates) continue;
        if (kerf.longitudinalSpringIndices && kerf.longitudinalSpringIndices.length === kerf.bridgeIndices.length) {
          for (let j = 0; j < kerf.bridgeIndices.length; j++) {
            const localIdx = kerf.longitudinalSpringIndices[j];
            const isBroken = kerfStates[localIdx];
            if (!isBroken && kerf.bridgeIndices[j]) {
              dyn.buffer.set(kerf.bridgeIndices[j], offset);
              offset += kerf.bridgeIndices[j].length;
            }
          }
        } else {
          for (let j = 0; j < kerf.springIndices.length; j++) {
            const isBroken = kerfStates[j];
            // If NOT broken, draw the bark bridge across the gap
            if (!isBroken && kerf.bridgeIndices[j]) {
              dyn.buffer.set(kerf.bridgeIndices[j], offset);
              offset += kerf.bridgeIndices[j].length;
            }
          }
        }
      }
      const barkEnd = offset;
      
      // Top Endcaps
      dyn.buffer.set(dyn.staticIndices.top, offset);
      offset += dyn.staticIndices.top.length;
      const topEndcapEnd = offset;
      
      // Bottom Endcaps + Exposed Kerf Endcaps
      dyn.buffer.set(dyn.staticIndices.bottom, offset);
      offset += dyn.staticIndices.bottom.length;
      
      for (const kerf of dyn.kerfs) {
        const kerfStates = brokenKerfSprings[kerf.globalKerfIndex];
        if (!kerfStates) continue;
        if (kerf.longitudinalSpringIndices && kerf.longitudinalSpringIndices.length === kerf.endcapIndices.length) {
          for (let j = 0; j < kerf.endcapIndices.length; j++) {
            const localIdx = kerf.longitudinalSpringIndices[j];
            const isBroken = kerfStates[localIdx];
            if (isBroken && kerf.endcapIndices[j]) {
              dyn.buffer.set(kerf.endcapIndices[j], offset);
              offset += kerf.endcapIndices[j].length;
            }
          }
        } else {
          for (let j = 0; j < kerf.springIndices.length; j++) {
            const isBroken = kerfStates[j];
            // If broken, expose the inner wood (endcap triangles)
            if (isBroken && kerf.endcapIndices[j]) {
              dyn.buffer.set(kerf.endcapIndices[j], offset);
              offset += kerf.endcapIndices[j].length;
            }
          }
        }
      }
      const bottomEndcapEnd = offset;
      const hash = `${barkEnd}_${topEndcapEnd}_${bottomEndcapEnd}`;
      if (geometry.userData.lastIndexHash !== hash) {
        geometry.userData.lastIndexHash = hash;
        geometry.setDrawRange(0, bottomEndcapEnd);
        geometry.clearGroups();
        geometry.addGroup(0, barkEnd, 0); // Bark
        geometry.addGroup(barkEnd, topEndcapEnd - barkEnd, 1); // Top
        geometry.addGroup(topEndcapEnd, bottomEndcapEnd - topEndcapEnd, 2); // Bottom
        geometry.index!.needsUpdate = true;
      }
    }
    
    // 2. Vertex Update
    const posAttribute = geometry.attributes.position;
    
    let colorAttribute = geometry.attributes.color;
    if (strains && !colorAttribute) {
       colorAttribute = new THREE.BufferAttribute(new Float32Array(posAttribute.count * 3), 3);
       geometry.setAttribute('color', colorAttribute);
    }

    let tensionThreshold = MIN_STRAIN_THRESHOLD;
    let compressionThreshold = MIN_STRAIN_THRESHOLD;

    if (strains) {
       let posCount = 0;
       let negCount = 0;
       for (let i = 0; i < strains.length; i++) {
          if (!globalTension && !entity.vertexMap[i]) continue; // Analyze nodes depending on scope
          if (entity.endcapNodeIndices && entity.endcapNodeIndices.has(i)) continue; // Exclude cut endcaps from scale threshold
          
          const s = strains[i];
          if (s > 0) {
             if (posCount >= this.scratchPositiveStrains.length) {
                const next = new Float32Array(this.scratchPositiveStrains.length * 2);
                next.set(this.scratchPositiveStrains);
                this.scratchPositiveStrains = next;
             }
             this.scratchPositiveStrains[posCount++] = s;
          } else if (s < 0) {
             if (negCount >= this.scratchNegativeStrains.length) {
                const next = new Float32Array(this.scratchNegativeStrains.length * 2);
                next.set(this.scratchNegativeStrains);
                this.scratchNegativeStrains = next;
             }
             this.scratchNegativeStrains[negCount++] = -s;
          }
       }

       if (posCount > 0) {
          const index = Math.min(Math.floor(posCount * PERCENTILE_TARGET), posCount - 1);
          const val = quickselect(this.scratchPositiveStrains, 0, posCount - 1, index);
          tensionThreshold = Math.max(val, MIN_STRAIN_THRESHOLD);
       }

       if (negCount > 0) {
          const index = Math.min(Math.floor(negCount * PERCENTILE_TARGET), negCount - 1);
          const val = quickselect(this.scratchNegativeStrains, 0, negCount - 1, index);
          compressionThreshold = Math.max(val, MIN_STRAIN_THRESHOLD);
       }
    }

    for (let nodeIdx = 0; nodeIdx < positions.length / 3; nodeIdx++) {
      const x = positions[nodeIdx * 3];
      const y = positions[nodeIdx * 3 + 1];
      const z = positions[nodeIdx * 3 + 2];
      
      let r = 1, g = 1, b = 1;
      if (strains) {
         const strain = strains[nodeIdx];
         if (strain < 0) { // Compression (Red)
            const intensity = Math.min(-strain / compressionThreshold, 1.0);
            g = 1 - intensity;
            b = 1 - intensity;
         } else if (strain > 0) { // Tension (Blue)
            const intensity = Math.min(strain / tensionThreshold, 1.0);
            r = 1 - intensity;
            g = 1 - intensity;
         }
      }

      const vertexIndices = entity.vertexMap[nodeIdx];
      if (vertexIndices) {
        for (const vIdx of vertexIndices) {
          posAttribute.setXYZ(vIdx, x, y, z);
          if (colorAttribute) {
             colorAttribute.setXYZ(vIdx, r, g, b);
          }
        }
      }
    }

    posAttribute.needsUpdate = true;
    if (colorAttribute) colorAttribute.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();

    // Update global debug springs mesh once per sync
    if (id === 'log-0' && this.globalSprings && this.globalSpringsMesh) {
      const lineGeom = this.globalSpringsMesh.geometry as THREE.BufferGeometry;
      
      if (!lineGeom.attributes.position || lineGeom.attributes.position.array.length !== positions.length) {
         lineGeom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
         
         // Clone globalSprings because we will mutate it to hide broken lines
         this.activeSprings = new Uint32Array(this.globalSprings.buffer).slice();
         lineGeom.setIndex(new THREE.BufferAttribute(this.activeSprings, 1));
      } else {
         const linePos = lineGeom.attributes.position as THREE.BufferAttribute;
         linePos.array.set(positions);
         linePos.needsUpdate = true;
      }

      // Update active springs based on brokenKerfSprings
      if (this.activeSprings && brokenKerfSprings) {
         let indexDirty = false;
         for (const e of this.getAll()) {
            if (e.type === 'log' && e.dynamicIndices) {
               for (const kerf of e.dynamicIndices.kerfs) {
                  const kerfStates = brokenKerfSprings[kerf.globalKerfIndex];
                  if (!kerfStates) continue;
                  for (let j = 0; j < kerf.springIndices.length; j++) {
                     const sIdx = kerf.springIndices[j];
                     if (kerfStates[j]) {
                        // Hide the spring by making it a degenerate line (0 -> 0)
                        if (this.activeSprings[sIdx * 2] !== 0 || this.activeSprings[sIdx * 2 + 1] !== 0) {
                           this.activeSprings[sIdx * 2] = 0;
                           this.activeSprings[sIdx * 2 + 1] = 0;
                           indexDirty = true;
                        }
                     }
                  }
               }
            }
         }
         if (indexDirty) {
            lineGeom.index!.needsUpdate = true;
         }
      }
    }
  }
}
